import type { ReactElement } from 'react';
import React, { useState } from 'react';
import {
  Box,
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  FormTitle,
  Input,
  ModalFooter,
  Text,
  View,
} from '../ui';
import { Controller, useForm } from 'react-hook-form';
import * as zod from 'zod';
import LocationSelect from '../LocationSelect';
import startCase from 'lodash/startCase';
import moment from 'moment';
import type { OpenPositionsFragment } from '../../graphql';
import {
  useCreateOpenPositionMutation,
  useUpdateOpenPositionMutation,
} from '../../graphql';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import LoadingSpinner from '../LoadingSpinner';
import { valueOrNull } from '../../lib/zod-util';

const locationSchema = zod.object({ value: zod.number() });

const _openPositionSchema = zod.object({
  startTime: zod.string(),
  startPort: locationSchema,
  direction: zod.object({ value: zod.number() }).optional(),
  remark: zod.string().optional(),
});

type OpenPositionFormFields = zod.infer<typeof _openPositionSchema>;

export interface OpenPositionFormProps {
  vesselId: number;
  onClose: () => void;
  initialStartTime?: number;
  openPosition?: OpenPositionsFragment;
  scheduleContext: ScheduleDataContext;
  lastItemEnd?: number;
  lastPortId?: number;
}

const OpenPositionForm = ({
  onClose,
  vesselId,
  openPosition,
  scheduleContext,
  lastItemEnd,
  lastPortId,
}: OpenPositionFormProps): ReactElement => {
  const [
    createOpenPositionMutation,
    { loading: saveLoading, error: saveError },
  ] = useCreateOpenPositionMutation();
  const [
    updateOpenPositionMutation,
    { loading: updateLoading, error: updateError },
  ] = useUpdateOpenPositionMutation();
  const isLoading = saveLoading || updateLoading;
  const startTime = openPosition?.startTime;
  const [checked, setChecked] = useState(
    startTime
      ? moment(startTime).valueOf() ===
          moment(lastItemEnd).startOf('hour').valueOf()
      : false,
  );
  const [timezone, setTimezone] = useState<string>('UTC');
  const isEdit = !!openPosition?.id;

  const startPort =
    openPosition?.startPortId !== undefined
      ? scheduleContext.locations.get(openPosition.startPortId)
      : null;
  const direction =
    openPosition?.directionId != null
      ? scheduleContext.locations.get(openPosition.directionId)
      : null;
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<OpenPositionFormFields>({
    defaultValues: {
      startTime: startTime
        ? moment(startTime).format()
        : moment(lastItemEnd).startOf('hour').add(1, 'hour').format(),
      startPort: { value: startPort?.id },
      direction: { value: direction?.id },
      remark: openPosition?.remark ?? '',
    },
  });

  const errorKeys = Object.keys(errors);
  const saveErrors = saveError || updateError;

  const onSubmit = async (data: OpenPositionFormFields) => {
    const input = {
      // From form
      startTime: data.startTime,
      startPortId: data.startPort.value,
      directionId: data.direction?.value,
      // From existing or defaults
      vesselId: openPosition?.vesselId ?? vesselId,
      remark: data.remark,
    };

    const id = openPosition?.id;
    if (id) {
      const result = await updateOpenPositionMutation({
        variables: {
          input: {
            id,
            openPositionPatch: input,
          },
        },
      });
      if (!result.errors?.length) {
        onClose();
      }
    } else {
      const result = await createOpenPositionMutation({
        variables: {
          input: {
            openPosition: input,
          },
        },
      });
      if (!result.errors?.length) {
        onClose();
      }
    }
  };

  return (
    <View height="100%">
      <Form
        style={{ overflowY: 'visible', borderBottom: 'none' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormSection
          borderBottomStyle="none"
          marginTop={2}
          marginLeft={-2}
          marginBottom={-3}
        >
          <FormTitle>
            <Text fontSize="1.4em" fontWeight="bold">
              {isEdit ? 'Edit open position' : 'New open position'}
            </Text>
          </FormTitle>
        </FormSection>
        <FormSection zIndex={4} borderBottomStyle="none">
          <FormRow>
            <FormField
              required={!checked}
              label="Start time"
              width="100%"
              hasError={!!errors.startTime}
            >
              <Box as="label" display="flex" alignItems="center">
                <input
                  style={{ margin: '10px 5px 10px 0' }}
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    setChecked(!checked);
                    setValue(
                      'startTime',
                      moment(lastItemEnd)
                        .startOf('hour')
                        .add(1, 'hour')
                        .format(),
                    );
                    if (lastPortId)
                      setValue('startPort', { value: lastPortId });

                    setTimezone(startPort?.timezone ?? 'UTC');
                  }}
                />
                Right after previous voyage
              </Box>
            </FormField>
            <FormField
              label="Start time"
              width="100%"
              required
              hasError={!!errors.startTime}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="startTime"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    onChange={onChange}
                    selected={value ? moment(value).toDate() : null}
                    showTime
                    showTimeInput
                    timeZone={timezone}
                    disabled={checked}
                  />
                )}
                rules={{ required: true }}
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              data-testid="open port"
              label="Open port"
              width="100%"
              required
              hasError={!!errors.startPort}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="startPort"
                render={({ field }) => (
                  <LocationSelect {...field} isDisabled={checked} />
                )}
                rules={{ required: true }}
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              data-testid="direction"
              label="Direction"
              width="100%"
              hasError={!!errors.direction}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="direction"
                render={({ field }) => <LocationSelect {...field} />}
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Remarks" width="98%" hasError={!!errors.remark}>
              <Input
                {...register('remark', { setValueAs: valueOrNull })}
                type="text"
              />
            </FormField>
          </FormRow>
        </FormSection>

        {errorKeys.length > 0 && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">
              {errorKeys
                .map((key) => `${startCase(key)} is not valid.`)
                .join(' ')}
            </Text>
          </FormSection>
        )}

        {isLoading && (
          <FormSection borderBottomStyle="none">
            <View flexDirection="row">
              <Text mr={3}>Saving changes</Text>
              <LoadingSpinner />
            </View>
          </FormSection>
        )}

        {saveErrors && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">{saveErrors.name}</Text>
            <Text color="#9d0300">{saveErrors.message}</Text>
            <Text color="#9d0300">
              {saveErrors.clientErrors
                .map((e) => `${e.name}: ${e.message}`)
                .join(' \n')}
            </Text>
            <Text color="#9d0300">
              {saveErrors.graphQLErrors
                .map((e) => `${e.name}: ${e.message}`)
                .join(' \n')}
            </Text>
          </FormSection>
        )}
      </Form>

      <div style={{ flexGrow: 1 }} />

      <ModalFooter>
        {onClose && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={onClose}
            marginRight="3"
          >
            Discard changes & Close
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        <Button disabled={isLoading} onPress={handleSubmit(onSubmit)}>
          {'Save & Close'}
        </Button>
      </ModalFooter>
    </View>
  );
};

export default OpenPositionForm;
